import { type getSelectProps, useInputControl } from '@conform-to/react'
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp'
import React, { useId } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Alert, AlertDescription } from './ui/alert.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import { Field as UiField, FieldError, type FieldProps } from './ui/field.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp.tsx'
import { Input } from './ui/input.tsx'
import {
	Select,
	SelectContent,
	SelectTrigger,
	SelectValue,
} from './ui/select.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map((e) => (
				<li key={e} className="text-error-text text-body-2xs">
					{e}
				</li>
			))}
		</ul>
	)
}

/**
 * Form-level errors (conform's `form.errors`) in an error `Alert`. Pass
 * `form.errorId` as `id` so the form's `aria-describedby` still resolves.
 * Per-field errors stay on `ErrorList`.
 */
export function FormErrorAlert({
	id,
	errors,
	className,
}: {
	errors?: ListOfErrors
	id?: string
	className?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<Alert tone="error" id={id} className={className}>
			{errorsToRender.map((e) => (
				<AlertDescription key={e}>{e}</AlertDescription>
			))}
		</Alert>
	)
}

/**
 * The `error` ui/field renders for a list of errors, or `undefined` when none
 * of them is set, so no slot is reserved for an absent error.
 */
function fieldError(errors: ListOfErrors) {
	return errors?.some(Boolean) ? <ErrorList errors={errors} /> : undefined
}

/** What the adapters read from `labelProps`; the id comes from the control. */
type FieldLabelProps = Pick<
	React.LabelHTMLAttributes<HTMLLabelElement>,
	'htmlFor' | 'children'
>

/**
 * Field, OTPField, TextareaField and SelectField keep the conform-friendly
 * `labelProps`/`inputProps`/`errors` API and render through ui/field's
 * `Field`, the one /styleguide catalogues.
 *
 * Their routes stack them without a gap, so each keeps the 32px bottom spacing
 * the old error reserve gave; an error now pushes the next field down instead
 * of filling a reserved slot. The `pb-8` goes once the form stacks own their
 * gap.
 *
 * ConformField itself is exported for a control none of them covers, such as
 * an input with a button beside it: pass a function child, as on ui/field.
 */
export function ConformField({
	labelProps,
	id,
	description,
	errors,
	required,
	className,
	children,
}: {
	labelProps: FieldLabelProps
	id: string | undefined
	description?: React.ReactNode
	errors?: ListOfErrors
	required?: boolean
	className?: string
	children: FieldProps['children']
}) {
	const fallbackId = useId()
	return (
		<UiField
			label={labelProps.children}
			htmlFor={id ?? labelProps.htmlFor ?? fallbackId}
			description={description}
			error={fieldError(errors)}
			required={required}
			className={cn('pb-8', className)}
		>
			{children}
		</UiField>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	required,
	className,
}: {
	labelProps: FieldLabelProps
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	required?: boolean
	className?: string
}) {
	return (
		<ConformField
			labelProps={labelProps}
			id={inputProps.id}
			errors={errors}
			required={required}
			className={className}
		>
			<Input {...inputProps} />
		</ConformField>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: FieldLabelProps
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	return (
		<ConformField
			labelProps={labelProps}
			id={inputProps.id}
			errors={errors}
			className={className}
		>
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
		</ConformField>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	required,
	className,
}: {
	labelProps: FieldLabelProps
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	required?: boolean
	className?: string
}) {
	return (
		<ConformField
			labelProps={labelProps}
			id={textareaProps.id ?? textareaProps.name}
			errors={errors}
			required={required}
			className={className}
		>
			<Textarea {...textareaProps} />
		</ConformField>
	)
}

/**
 * A `ui/select` bound to a conform field: pass `getSelectProps(field)` as
 * `selectProps` and the `SelectItem`s as children. conform's
 * `useInputControl` keeps a hidden native select in the form, so the value
 * submits under the field name. Radix reserves `''` for "nothing selected",
 * so an item cannot carry an empty value.
 */
export function SelectField({
	labelProps,
	selectProps,
	placeholder,
	description,
	errors,
	required,
	className,
	children,
}: {
	labelProps: FieldLabelProps
	selectProps: ReturnType<typeof getSelectProps>
	placeholder?: string
	description?: React.ReactNode
	errors?: ListOfErrors
	required?: boolean
	className?: string
	children: React.ReactNode
}) {
	const { key, id, name, form, defaultValue } = selectProps
	const input = useInputControl({
		key,
		name,
		formId: form,
		initialValue: defaultValue == null ? undefined : String(defaultValue),
	})
	return (
		<ConformField
			labelProps={labelProps}
			id={id}
			description={description}
			errors={errors}
			required={required}
			className={className}
		>
			{(control) => (
				<Select
					value={input.value ?? ''}
					onValueChange={input.change}
					onOpenChange={(open) => {
						if (!open) input.blur()
					}}
				>
					<SelectTrigger {...control} onFocus={input.focus}>
						<SelectValue placeholder={placeholder} />
					</SelectTrigger>
					<SelectContent>{children}</SelectContent>
				</Select>
			)}
		</ConformField>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: React.ComponentProps<'label'>
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const error = fieldError(errors)
	const errorId = error ? `${id}-error` : undefined

	// Checkbox beside its label, so ui/field's stacked layout does not fit;
	// the error still renders in its `field-error` slot, aligned under the label.
	// `pb-4` is the 16px the old unreserved error slot took.
	return (
		<div className={cn('flex flex-col gap-1 pb-4', className)}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="text-body-xs text-muted-foreground self-center"
				/>
			</div>
			{error ? (
				<FieldError id={errorId} className="pl-6">
					{error}
				</FieldError>
			) : null}
		</div>
	)
}
